<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
            <!-- Header -->
            <div class="tw-relative tw-bg-gradient-to-br tw-from-primary-50 tw-to-primary-100/50 tw-px-6 tw-pt-8 tw-pb-6">
                <div class="tw-absolute tw-top-4 tw-right-4">
                    <button @click="onCancel" class="tw-p-2 tw-rounded-full hover:tw-bg-white/30 tw-transition-colors">
                        <icon icon="solar:close-circle-bold" class="tw-text-xl tw-text-gray-600" />
                    </button>
                </div>
                
                <div class="tw-flex tw-items-center tw-gap-3">
                    <div class="tw-p-2 tw-bg-white/80 tw-rounded-xl">
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-2xl tw-text-primary-500" />
                    </div>
                    <div>
                        <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">
                            {{ $t('buttons.book-meeting') }}
                        </h2>
                        <p class="tw-text-sm tw-text-gray-600 tw-mt-1">
                            {{ $t('messages.book-meeting-with', { name: targetUser?.name || $t('common.user') }) }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Form Content -->
            <div class="tw-px-6 tw-py-6 tw-space-y-4">
                <!-- Title Field -->
                <FormField
                    v-model="formData.title"
                    type="text"
                    :label="$t('fields.title')"
                    :placeholder="$t('forms.meeting-title-placeholder', 'Meeting Title')"
                    left-icon="solar:document-text-bold-duotone"
                    :required="true"
                    :error="errors.title"
                    :help="$t('forms.meeting-title-help', 'Enter a descriptive title for your meeting')"
                />

                <!-- Scheduled Date & Time -->
                <FormField
                    v-model="formData.scheduled_at"
                    type="datetime-local"
                    :label="$t('fields.scheduled-at', 'Scheduled Date & Time')"
                    left-icon="solar:calendar-mark-bold-duotone"
                    :required="true"
                    :error="errors.scheduled_at"
                    :help="$t('forms.scheduled-at-help', 'Select when the meeting will take place')"
                />

                <!-- Duration -->
                <FormField
                    v-model="formData.duration_minutes"
                    type="number"
                    :label="$t('fields.duration', 'Duration (minutes)')"
                    :placeholder="$t('forms.duration-placeholder', 'e.g., 60')"
                    left-icon="solar:clock-circle-bold-duotone"
                    :error="errors.duration_minutes"
                    :help="$t('forms.duration-help', 'Meeting duration in minutes (15-480, optional)')"
                />

                <!-- Description -->
                <FormField
                    v-model="formData.description"
                    type="textarea"
                    :label="$t('fields.description')"
                    :placeholder="$t('forms.meeting-description-placeholder', 'Add any additional details about the meeting...')"
                    left-icon="solar:notes-bold-duotone"
                    :error="errors.description"
                    rows="4"
                />

                <!-- Timezone -->
                <FormField
                    v-model="formData.timezone"
                    type="text"
                    :label="$t('fields.timezone', 'Timezone')"
                    :placeholder="$t('forms.timezone-placeholder', 'e.g., UTC, America/New_York')"
                    left-icon="solar:global-bold-duotone"
                    :error="errors.timezone"
                    :help="$t('forms.timezone-help', 'Optional timezone identifier')"
                />
            </div>

            <!-- Footer -->
            <div class="tw-px-6 tw-pb-6 tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button 
                    intent="white" 
                    :disabled="isLoading" 
                    @click="onCancel"
                    class="tw-min-w-[100px]"
                >
                    {{ $t('buttons.cancel') }}
                </a-button>
                <a-button 
                    intent="primary" 
                    :disabled="isLoading || !isValid" 
                    :loading="isLoading" 
                    @click="handleSubmit"
                    left-icon="solar:calendar-bold-duotone"
                    class="tw-min-w-[100px]"
                >
                    {{ $t('buttons.book-meeting') }}
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import useAlert from '@/composables/useAlert';
import meetingsApi from '@/api/meetings';

const { t } = useI18n();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    targetUser: {
        required: true
    }
});

const emit = defineEmits(['close', 'meeting-created']);

// Form state
const formData = ref({
    title: '',
    scheduled_at: '',
    duration_minutes: null,
    description: '',
    timezone: ''
});

const errors = ref({
    title: '',
    scheduled_at: '',
    duration_minutes: '',
    description: '',
    timezone: ''
});

const isLoading = ref(false);

// Computed properties
const isValid = computed(() => {
    return formData.value.title.trim().length > 0 && 
           formData.value.title.trim().length <= 255 &&
           formData.value.scheduled_at.length > 0;
});

// Reset form when popup closes
watch(() => props.visible, (newValue) => {
    if (!newValue) {
        resetForm();
    }
});

// Methods
const resetForm = () => {
    formData.value = {
        title: '',
        scheduled_at: '',
        duration_minutes: null,
        description: '',
        timezone: ''
    };
    errors.value = {
        title: '',
        scheduled_at: '',
        duration_minutes: '',
        description: '',
        timezone: ''
    };
};

const validateForm = () => {
    let isValid = true;
    errors.value = {
        title: '',
        scheduled_at: '',
        duration_minutes: '',
        description: '',
        timezone: ''
    };

    // Validate title
    if (!formData.value.title.trim()) {
        errors.value.title = t('errors.field-required', 'This field is required');
        isValid = false;
    } else if (formData.value.title.trim().length > 255) {
        errors.value.title = t('errors.max-length', { max: 255 }, `Maximum length is 255 characters`);
        isValid = false;
    }

    // Validate scheduled_at
    if (!formData.value.scheduled_at) {
        errors.value.scheduled_at = t('errors.field-required', 'This field is required');
        isValid = false;
    } else {
        const scheduledDate = new Date(formData.value.scheduled_at);
        const now = new Date();
        if (scheduledDate <= now) {
            errors.value.scheduled_at = t('errors.date-must-be-future', 'The meeting must be scheduled in the future');
            isValid = false;
        }
    }

    // Validate duration_minutes if provided
    if (formData.value.duration_minutes !== null && formData.value.duration_minutes !== '') {
        const duration = parseInt(formData.value.duration_minutes);
        if (isNaN(duration) || duration < 15 || duration > 480) {
            errors.value.duration_minutes = t('errors.duration-range', 'Duration must be between 15 and 480 minutes');
            isValid = false;
        }
    }

    // Validate timezone if provided
    if (formData.value.timezone && formData.value.timezone.trim().length > 50) {
        errors.value.timezone = t('errors.max-length', { max: 50 }, `Maximum length is 50 characters`);
        isValid = false;
    }

    return isValid;
};

const handleSubmit = async () => {
    if (!validateForm()) return;
    
    isLoading.value = true;
    try {
        // Prepare API data
        const apiData = {
            user_id: props.targetUser.id,
            title: formData.value.title.trim(),
            scheduled_at: new Date(formData.value.scheduled_at).toISOString(),
        };

        // Add optional fields only if they have values
        if (formData.value.duration_minutes !== null && formData.value.duration_minutes !== '') {
            apiData.duration_minutes = parseInt(formData.value.duration_minutes);
        }
        if (formData.value.description && formData.value.description.trim()) {
            apiData.description = formData.value.description.trim();
        }
        if (formData.value.timezone && formData.value.timezone.trim()) {
            apiData.timezone = formData.value.timezone.trim();
        }

        const result = await meetingsApi.createMemberToMember(apiData);
        useAlert(t('messages.meeting-created-successfully', 'Meeting created successfully'), 'success');
        emit('meeting-created', result.data);
        onCancel();
    } catch (error) {
        console.error('Error creating meeting:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.errors?.scheduled_at?.[0] ||
                           error.message || 
                           t('errors.something-went-wrong', 'Something went wrong');
        useAlert(errorMessage, 'danger');
    } finally {
        isLoading.value = false;
    }
};

const onCancel = () => {
    resetForm();
    emit('close');
};
</script>

